// @ts-nocheck
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePlacesAutocomplete } from '@/hooks/usePlacesAutocomplete';
import { debounce } from 'lodash';

interface AddressAutocompleteProps {
  onAddressSelected: (addressData: {
    address1: string;
    city: string;
    state: string;
    country: string;
    zipcode: string;
    lon: number;
    lat: number;
  }) => void;
  initialAddress?: string;
  error?: string;
  required?: boolean;
  label?: string;
  onFocus?: () => void;
}

export const AddressAutocomplete = ({
  onAddressSelected,
  initialAddress = '',
  error,
  required = false,
  label = 'Address',
  onFocus,
}: AddressAutocompleteProps) => {
  const [addressQuery, setAddressQuery] = useState(initialAddress);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [clickedSuggestion, setClickedSuggestion] = useState<boolean>(false);

  const {
    predictions,
    loading,
    fetchPredictions,
    clearPredictions,
    getPlaceDetails,
  } = usePlacesAutocomplete();

  const debouncedFetch = useCallback(debounce(fetchPredictions, 400), [
    fetchPredictions,
  ]);

  // Logic for closing suggestions when clicking outside remains in the component,
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.address-autocomplete-box')) {
        clearPredictions();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clearPredictions]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setAddressQuery(value);
    debouncedFetch(value);
  };

  const handleSuggestionClick = async (suggestion: any) => {
    clearPredictions();

    getPlaceDetails(suggestion.place_id, (place: any, status: any) => {
      if (status === 'OK') {
        const addressComponents = place.address_components;
        let streetNumber = '';
        let streetName = '';
        let city = '';
        let state = '';
        let country = '';
        let zipcode = '';
        const lat = place.geometry?.location?.lat() || 0;
        const lon = place.geometry?.location?.lng() || 0;

        addressComponents.forEach((component: any) => {
          const types = component.types;
          if (types.includes('street_number'))
            streetNumber = component.long_name;
          else if (types.includes('route')) streetName = component.long_name;
          else if (types.includes('locality')) city = component.long_name;
          else if (types.includes('administrative_area_level_1'))
            state = component.short_name;
          else if (types.includes('country')) country = component.short_name;
          else if (types.includes('postal_code')) zipcode = component.long_name;
        });

        const address1 = `${streetNumber} ${streetName}`.trim();

        setAddressQuery(place.formatted_address);
        onAddressSelected({
          address1,
          city,
          state,
          country,
          zipcode,
          lon,
          lat,
        });
      } else {
        setErrorMessage(
          'Unable to get full address details. Please try again.',
        );
      }
    });
  };

  const handleFocus = () => {
    if (clickedSuggestion && onFocus) {
      onFocus();
      setClickedSuggestion(false);
    }
  };

  return (
    <div className="space-y-2 relative">
      <Label
        className="text-sm font-medium text-muted-foreground"
        htmlFor="address-autocomplete"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <div className="relative">
        <Input
          id="address-autocomplete"
          value={addressQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          // onKeyDown={handleKeyDown}
          placeholder="Start typing address..."
          className={`${error ? 'border-destructive' : ''} ${
            loading ? 'pr-10' : ''
          }`}
          autoComplete="off"
          aria-describedby={errorMessage ? 'address-error' : undefined}
          aria-invalid={!!error}
        />

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="h-4 w-4 border-2 border-border border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {predictions.length > 0 && (
        <div
          className="absolute z-10 bg-card border max-h-48 overflow-y-auto w-full shadow-md rounded address-autocomplete-box mt-1"
          role="listbox"
          aria-label="Address suggestions"
        >
          {predictions.map((s, i) => (
            <div
              key={s.place_id}
              role="option"
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm  `}
              onClick={() => handleSuggestionClick(s)}
            >
              {s.description}
            </div>
          ))}
        </div>
      )}

      {(error || errorMessage) && (
        <p id="address-error" className="text-sm font-medium text-destructive">
          {error || errorMessage}
        </p>
      )}
    </div>
  );
};
